
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface BandDisagreementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userPerformanceBand: any;
  currentUser: any;
  supervisorName: string;
  employeeName?: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const BandDisagreementDialog = ({
  isOpen,
  onClose,
  userPerformanceBand,
  currentUser,
  supervisorName,
  employeeName,
  onSuccess,
  onError
}: BandDisagreementDialogProps) => {
  const [requestedBand, setRequestedBand] = useState("");
  const [disagreementReason, setDisagreementReason] = useState("");
  const [supportingEvidence, setSupportingEvidence] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeYear } = usePerformanceYear();

  const handleSubmit = async () => {
    if (!requestedBand || !disagreementReason.trim()) {
      onError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get supervisor ID from performance band
      const supervisorId = userPerformanceBand?.supervisor_id;
      
      if (!supervisorId) {
        onError("Supervisor information not found");
        setIsSubmitting(false);
        return;
      }

      // Create record in band_disagreements table
      const { error: disagreementError } = await supabase
        .from('band_disagreements')
        .insert({
          user_id: currentUser.id,
          supervisor_id: supervisorId,
          final_band: userPerformanceBand.final_banding,
          requested_band: requestedBand,
          disagreement_reason: disagreementReason,
          supporting_evidence: supportingEvidence || null,
          status: 'submitted',
          employee_name: employeeName || null,
          supervisor_name: supervisorName || null,
          year: activeYear
        });

      if (disagreementError) {
        console.error("Error submitting band disagreement:", disagreementError);
        onError("Failed to submit disagreement. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Update via SECURITY DEFINER RPC — employees can only change status.
      const { error: updateError } = await supabase.rpc('set_performance_band_status', {
        p_year: activeYear,
        p_status: 'Band disagreement submitted',
      });

      if (updateError) {
        console.error('Error updating performance band status:', updateError);
        onError("Failed to update performance band status. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Reset form
      setRequestedBand("");
      setDisagreementReason("");
      setSupportingEvidence("");
      setIsSubmitting(false);
      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error submitting band disagreement:", error);
      onError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRequestedBand("");
      setDisagreementReason("");
      setSupportingEvidence("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-secondary border-primary text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-primary">Submit Band Disagreement ({activeYear})</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white text-sm">Current Band</Label>
            <div className="bg-white/10 border border-primary/30 rounded px-3 py-2 text-white text-sm">
              {userPerformanceBand?.final_banding || 'N/A'}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white text-sm">Requested Band *</Label>
            <Select value={requestedBand} onValueChange={setRequestedBand}>
              <SelectTrigger className="bg-white/10 border-primary/30 text-white">
                <SelectValue placeholder="Select requested band" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">Band A</SelectItem>
                <SelectItem value="B">Band B</SelectItem>
                <SelectItem value="C">Band C</SelectItem>
                <SelectItem value="D">Band D</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white text-sm">Disagreement Reason *</Label>
            <Textarea
              value={disagreementReason}
              onChange={(e) => setDisagreementReason(e.target.value)}
              placeholder="Please explain why you disagree with your current band..."
              className="bg-white/10 border-primary/30 text-white min-h-[80px] resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white text-sm">Supporting Evidence (Optional)</Label>
            <Textarea
              value={supportingEvidence}
              onChange={(e) => setSupportingEvidence(e.target.value)}
              placeholder="Provide any supporting evidence for your disagreement..."
              className="bg-white/10 border-primary/30 text-white min-h-[60px] resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="bg-transparent border-gray-500 text-gray-300 hover:bg-gray-500/20"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !requestedBand || !disagreementReason.trim()}
            className="bg-primary hover:bg-primary/80 text-black"
          >
            {isSubmitting ? "Submitting..." : "Submit Disagreement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BandDisagreementDialog;
